import React, { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link,
  Stack,
  MenuItem,
  Select,
  type SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material'
import { BarChart, Refresh } from '@mui/icons-material'
import {
  Area,
  AreaChart,
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import type { AdminPalworldJoinEvent } from '../../types'
import { apiService } from '../../services/api'

const cardSx = {
  bgcolor: '#0c0e1c',
  backgroundImage: 'radial-gradient(1000px 520px at 20% 0%, rgba(255,255,255,0.07), rgba(255,255,255,0) 55%)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 4,
  boxShadow: '0 18px 60px rgba(0,0,0,0.55)'
}

const tooltipProps = {
  contentStyle: {
    background: 'rgba(12,14,28,0.98)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 14,
    boxShadow: '0 18px 50px rgba(0,0,0,0.55)'
  } as any,
  itemStyle: { color: 'rgba(255,255,255,0.88)' } as any,
  labelStyle: { color: 'rgba(255,255,255,0.75)' } as any
}

const safeText = (v: unknown): string => {
  if (typeof v === 'string' && v.trim()) return v.trim()
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return '—'
}

type PlayerKey = string
type PlayerAgg = {
  key: PlayerKey
  name: string
  userId: string | null
  count: number
}

const DAY_LABEL: Record<number, string> = {
  0: 'Domingo',
  1: 'Segunda',
  2: 'Terça',
  3: 'Quarta',
  4: 'Quinta',
  5: 'Sexta',
  6: 'Sábado'
}
const DAY_ORDER: number[] = [1, 2, 3, 4, 5, 6, 0]

const getWeekdayIndex = (createdAt: string | undefined | null): number | null => {
  if (!createdAt) return null
  const d = new Date(createdAt)
  if (Number.isNaN(d.getTime())) return null
  return d.getDay()
}

const palette = ['#00e676', '#2196f3', '#ff9800', '#9c27b0', '#f44336', '#795548', '#607d8b', '#e91e63', '#3f51b5', '#009688']

const formatCompact = (n: number) => {
  if (!Number.isFinite(n)) return '0'
  try {
    return new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(n)
  } catch {
    return n.toLocaleString('pt-BR')
  }
}

const toHourLabel = (h: number) => `${h}h`

const AdminEstatisticasPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [rows, setRows] = useState<AdminPalworldJoinEvent[]>([])
  const [rowCount, setRowCount] = useState(0)

  const [refreshKey, setRefreshKey] = useState(0)

  const [query, setQuery] = useState('')
  const [topN, setTopN] = useState(5)
  const [dataInicial, setDataInicial] = useState('2026-02-07')
  const [dataFinal, setDataFinal] = useState('')

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const pageSize = 500
        const first = await apiService.listarPalworldJoinsAdmin({
          page: 0,
          size: pageSize,
          sort: 'created_at',
          direction: 'desc',
          dataInicial: dataInicial.trim() ? dataInicial.trim() : undefined,
          dataFinal: dataFinal.trim() ? dataFinal.trim() : undefined
        })
        if (!mounted) return
        const all: AdminPalworldJoinEvent[] = Array.isArray(first?.content) ? [...first.content] : []
        const totalElements = Number(first?.totalElements ?? 0) || 0
        const totalPages = Number(first?.totalPages ?? 0) || 0

        setRowCount(totalElements)

        if (totalPages > 1) {
          for (let page = 1; page < totalPages; page++) {
            if (!mounted) return
            const res = await apiService.listarPalworldJoinsAdmin({
              page,
              size: pageSize,
              sort: 'created_at',
              direction: 'desc',
              dataInicial: dataInicial.trim() ? dataInicial.trim() : undefined,
              dataFinal: dataFinal.trim() ? dataFinal.trim() : undefined
            })
            if (!mounted) return
            const content: AdminPalworldJoinEvent[] = Array.isArray(res?.content) ? res.content : []
            all.push(...content)
          }
        }

        if (!mounted) return
        setRows(all)
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao consultar entradas (joins).')
        setRows([])
        setRowCount(0)
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    run()

    return () => {
      mounted = false
    }
  }, [dataFinal, dataInicial, refreshKey])

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) => {
      const name = String(r.playerName ?? '').toLowerCase()
      return name.includes(q)
    })
  }, [query, rows])

  const topPlayers = useMemo<PlayerAgg[]>(() => {
    const map = new Map<PlayerKey, PlayerAgg>()
    for (const r of filteredRows) {
      const name = safeText(r.playerName)
      const userId = r.userId ?? null
      const key = userId ? `uid:${userId}` : `anon:${name}`
      const prev = map.get(key)
      if (!prev) {
        map.set(key, { key, name, userId, count: 1 })
      } else {
        prev.count += 1
      }
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count)
  }, [filteredRows])

  const weekdayTotals = useMemo(() => {
    const counts: Record<number, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
    for (const r of filteredRows) {
      const idx = getWeekdayIndex(r.createdAt)
      if (idx === null) continue
      counts[idx] = (counts[idx] ?? 0) + 1
    }
    return DAY_ORDER.map((dayIdx) => ({ dayIdx, day: DAY_LABEL[dayIdx], total: counts[dayIdx] ?? 0 }))
  }, [filteredRows])

  const hourTotals = useMemo(() => {
    const counts = Array.from({ length: 24 }, (_, hour) => ({ hour, label: toHourLabel(hour), total: 0 }))
    for (const r of filteredRows) {
      const createdAt = r.createdAt
      if (!createdAt) continue
      const d = new Date(createdAt)
      if (Number.isNaN(d.getTime())) continue
      const hour = d.getHours()
      const target = counts[hour]
      if (!target) continue
      target.total += 1
    }
    return counts
  }, [filteredRows])

  const topPlayersData = useMemo(() => {
    return topPlayers.slice(0, Math.max(1, topN)).map((p) => ({ name: p.name, count: p.count, userId: p.userId }))
  }, [topN, topPlayers])

  const handleTopNChange = (e: SelectChangeEvent<string>) => {
    const v = Number(e.target.value)
    setTopN(Number.isFinite(v) && v > 0 ? v : 5)
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BarChart />
              Estatísticas
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>Entradas de jogadores (joins) no servidor.</Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={() => setRefreshKey((v) => v + 1)}
            startIcon={<Refresh />}
            disabled={loading}
          >
            Atualizar
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'rgba(255,255,255,0.72)' }}>
                  TOTAL DE ACESSOS
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 1000, mt: 0.5 }}>
                  {formatCompact(rowCount)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5 }}>
                  Registros carregados: {rows.length.toLocaleString('pt-BR')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'rgba(255,255,255,0.72)' }}>
                  JOGADORES ÚNICOS
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 1000, mt: 0.5 }}>
                  {formatCompact(topPlayers.length)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5 }}>
                  Baseado no filtro atual
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card sx={cardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'rgba(255,255,255,0.72)' }}>
                  ACESSOS (FILTRO)
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 1000, mt: 0.5 }}>
                  {formatCompact(filteredRows.length)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5 }}>
                  Nome: {query.trim() ? `"${query.trim()}"` : 'todos'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 3 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between' }}>
              <TextField
                size="small"
                label="Filtrar por nome"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ex: iYoshi_Aki"
                sx={{ maxWidth: 520, width: '100%' }}
              />
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Data inicial"
                  type="date"
                  value={dataInicial}
                  onChange={(e) => setDataInicial(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="Data final"
                  type="date"
                  value={dataFinal}
                  onChange={(e) => setDataFinal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Opcional"
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    Top
                  </Typography>
                  <Select size="small" value={String(topN)} onChange={handleTopNChange} sx={{ minWidth: 140 }}>
                    <MenuItem value="5">Top 5</MenuItem>
                    <MenuItem value="10">Top 10</MenuItem>
                    <MenuItem value="15">Top 15</MenuItem>
                  </Select>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ ...cardSx, position: 'relative' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Top {topN} Jogadores
                </Typography>
                <Box sx={{ height: 360 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RBarChart data={topPlayersData} margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                      <XAxis
                        dataKey="name"
                        interval={0}
                        tick={{ fill: 'rgba(255,255,255,0.72)', fontSize: 12 }}
                        tickFormatter={(v) => (String(v).length > 12 ? `${String(v).slice(0, 12)}…` : String(v))}
                      />
                      <YAxis allowDecimals={false} tick={{ fill: 'rgba(255,255,255,0.55)' }} />
                      <Tooltip {...tooltipProps} />
                      <Bar dataKey="count" name="Acessos" radius={[10, 10, 0, 0]}>
                        {topPlayersData.map((_, i) => (
                          <Cell key={i} fill={palette[i % palette.length]} />
                        ))}
                      </Bar>
                    </RBarChart>
                  </ResponsiveContainer>
                </Box>

                <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', mt: 2 }}>
                  {topPlayersData.map((p, i) => (
                    <Box key={`${p.userId ?? p.name}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: palette[i % palette.length] }} />
                      {p.userId ? (
                        <Link
                          component={RouterLink}
                          to={`/admin/usuarios/${encodeURIComponent(p.userId)}`}
                          underline="hover"
                          sx={{ color: 'rgba(255,255,255,0.86)', fontWeight: 900 }}
                        >
                          {p.name}
                        </Link>
                      ) : (
                        <Typography sx={{ color: 'rgba(255,255,255,0.86)', fontWeight: 900 }}>{p.name}</Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.58)' }}>
                        ({p.count})
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>

              {loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,0.18)', zIndex: 1 }}>
                  <CircularProgress />
                </Box>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ ...cardSx, position: 'relative' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Acessos por Hora do Dia
                </Typography>
                <Box sx={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={hourTotals} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <defs>
                        <linearGradient id="joinsArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00e676" stopOpacity={0.45} />
                          <stop offset="95%" stopColor="#00e676" stopOpacity={0.03} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                      <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.72)', fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fill: 'rgba(255,255,255,0.55)' }} />
                      <Tooltip {...tooltipProps} />
                      <Area type="monotone" dataKey="total" name="Acessos" stroke="#00e676" strokeWidth={2} fill="url(#joinsArea)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>

              {loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,0.18)', zIndex: 1 }}>
                  <CircularProgress />
                </Box>
              )}
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ ...cardSx, position: 'relative' }}>
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
                  Acessos por Dia da Semana
                </Typography>
                <Box sx={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RBarChart data={weekdayTotals} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.18} />
                      <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.72)', fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fill: 'rgba(255,255,255,0.55)' }} />
                      <Tooltip {...tooltipProps} />
                      <Bar dataKey="total" name="Acessos" radius={[10, 10, 0, 0]}>
                        {weekdayTotals.map((_, i) => (
                          <Cell key={i} fill={palette[i % palette.length]} />
                        ))}
                      </Bar>
                    </RBarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>

              {loading && (
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,0.18)', zIndex: 1 }}>
                  <CircularProgress />
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  )
}

export default AdminEstatisticasPage
